/**
 * wa-template-definition.model.ts
 *
 * Web-side view of the WhatsApp Cloud API template contract exposed by trendmulti-api
 * (`/channel/templates/...`).
 *
 * The contract is NOT redeclared here — it is re-exported from `@trendbuild/trend-cloud-api`, the
 * same package version `trendmulti-api` validates against, so the limits the form enforces and the
 * limits the API enforces cannot drift. What stays local is UI-only material: the presentation order
 * of the language picker, the `accept` strings of the file input, a permissive read projection, and
 * a parsing helper.
 *
 * IMPORTANT — value imports use the deep path on purpose. Importing a *value* from the package root
 * pulls `base.service.js` into the browser bundle, and it requires node builtins (dns/http/https),
 * which breaks `ng build`. Type imports are erased and are safe from the root.
 * See specs/2026-08-whatsapp-cloud-api-template-builder/adr/ADR-001-provider-calls-server-side.md
 *
 * Documentation: https://developers.facebook.com/documentation/business-messaging/whatsapp/templates/components
 */

import type {
  BaseRequest,
  ButtonItemComponentObject,
  CategoryTemplateMessage,
  CreateTemplateRequest,
  DisplayFormatTemplateMessage,
  FormatHeaderComponentObject,
  NamedParameterExampleObject,
  ParameterFormatTemplateMessage,
  TemplateBodyDefinition,
  TemplateButtonDefinition,
  TemplateButtonDefinitionType,
  TemplateFooterDefinition,
  TemplateHeaderDefinition,
  TemplateMessageObject,
  TemplateParams,
  UpdateTemplateRequest,
} from '@trendbuild/trend-cloud-api';

import {
  ORDER_DETAILS_CATEGORIES,
  ORDER_DETAILS_HEADER_FORMATS,
  TEMPLATE_LIMITS,
  TEMPLATE_TTL_RANGES,
} from '@trendbuild/trend-cloud-api/src/lib/models/template/template-definition';
import { TEMPLATE_LANGUAGES, TemplateLanguage } from '@trendbuild/trend-cloud-api/src/lib/models/template/template-language';

// ---------------------------------------------------------------- contract (re-exported)

export type WaTemplateCategory = CategoryTemplateMessage;
export type WaTemplateParameterFormat = ParameterFormatTemplateMessage;
export type WaTemplateHeaderFormat = FormatHeaderComponentObject;
export type WaTemplateButtonType = TemplateButtonDefinitionType;
export type WaTemplateNamedExample = NamedParameterExampleObject;

/**
 * Alternative rendering of a template. `ORDER_DETAILS` is the Payments API - Brazil format: it is
 * what unlocks the ORDER_DETAILS button, and it is the only way to deliver a Pix code with a native
 * copy affordance — the payload travels as a button action parameter at send time, not as a body
 * variable, so it escapes both the 1024-character body budget and the 20-character COPY_CODE cap.
 */
export type WaTemplateDisplayFormat = DisplayFormatTemplateMessage;

export type WaTemplateHeaderDefinition = TemplateHeaderDefinition;
export type WaTemplateBodyDefinition = TemplateBodyDefinition;
export type WaTemplateFooterDefinition = TemplateFooterDefinition;
export type WaTemplateButtonDefinition = TemplateButtonDefinition;

/** Create payload — the provider credentials are resolved by the API, never sent by the client. */
export type WaTemplateDefinition = Omit<CreateTemplateRequest, keyof BaseRequest>;

/** Edit payload — name and language cannot be changed after creation. */
export type WaTemplateEditDefinition = Omit<UpdateTemplateRequest, keyof BaseRequest | 'templateID'>;

/** Read shape of a template returned by the Graph API. */
export type WaTemplateRead = TemplateMessageObject;

/**
 * Parameter values of a template SEND. POSITIONAL templates take an array matched by position;
 * NAMED templates take an object keyed by placeholder name (`trend-cloud-api` 2.4.0).
 */
export type WaTemplateParams = TemplateParams;

/** Named parameters must be lowercase letters, digits and underscores — the only shape Meta accepts. */
export const WA_TEMPLATE_NAMED_PARAMETER_PATTERN = /^[a-z0-9_]+$/;

export type WaTemplateLanguage = TemplateLanguage;

/** Limits documented by Meta and enforced by the provider client — single source of truth. */
export const WA_TEMPLATE_LIMITS = TEMPLATE_LIMITS;

/** Minimum and maximum message validity period, in seconds, accepted per category. */
export const WA_TEMPLATE_TTL_RANGES = TEMPLATE_TTL_RANGES;

/** Categories a template rendered as order details may be created under. */
export const WA_ORDER_DETAILS_CATEGORIES = ORDER_DETAILS_CATEGORIES;

/** Header formats a template rendered as order details may carry. */
export const WA_ORDER_DETAILS_HEADER_FORMATS = ORDER_DETAILS_HEADER_FORMATS;

// ---------------------------------------------------------------- local UI concerns

/**
 * Permissive projection over `ComponentObject`, which is a union. Reading a template component back
 * into the form touches fields that only exist on some members, so the form works against this flat
 * shape instead of narrowing the union at every access.
 */
export interface WaTemplateComponentRead {
  type: string;
  format?: string;
  text?: string;
  buttons?: Array<ButtonItemComponentObject>;
  example?: {
    header_text?: Array<string>;
    header_handle?: Array<string>;
    header_text_named_params?: Array<NamedParameterExampleObject>;
    body_text?: Array<Array<string>>;
    body_text_named_params?: Array<NamedParameterExampleObject>;
  };
}

export type WaTemplateButtonRead = ButtonItemComponentObject;

/** Response of the media upload endpoint — the API returns the asset handle already unwrapped. */
export interface WaTemplateMediaHandle {
  handle: string;
}

/** Media accepted by Meta on a template header, per header format — `accept` of the file input. */
export const WA_TEMPLATE_MEDIA_ACCEPT: { [format: string]: string } = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4,video/3gpp',
  DOCUMENT: 'application/pdf',
};

/**
 * Presentation order of the language picker: the languages this product's users pick most come
 * first, the rest keep the provider's order. The list itself is the provider's — only the ordering
 * is a UI decision.
 */
const PREFERRED_LANGUAGE_CODES: Array<string> = ['pt_BR', 'pt_PT', 'en', 'en_US', 'en_GB', 'es', 'es_AR', 'es_ES', 'es_MX'];

export const WA_TEMPLATE_LANGUAGES: Array<WaTemplateLanguage> = [
  ...PREFERRED_LANGUAGE_CODES
    .map((code) => TEMPLATE_LANGUAGES.find((language) => language.code === code))
    .filter((language) => !!language),
  ...TEMPLATE_LANGUAGES.filter((language) => PREFERRED_LANGUAGE_CODES.indexOf(language.code) === -1),
];

/**
 * Extracts the parameter names of a template string in the order they appear, without repetition.
 * `Olá {{nome}}, pedido {{pedido}} — obrigado {{nome}}!` returns `['nome', 'pedido']`.
 */
export function extractWaTemplateParameters(text?: string): Array<string> {
  if (!text) {
    return [];
  }

  const parameters: Array<string> = [];
  const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
  let match = regex.exec(text);
  while (match !== null) {
    if (parameters.indexOf(match[1]) === -1) {
      parameters.push(match[1]);
    }
    match = regex.exec(text);
  }

  return parameters;
}

/**
 * Format of a template as Meta records it. Falls back to the markers in the text when the listing
 * did not carry `parameter_format`: a single non-numeric marker means NAMED.
 */
export function resolveWaTemplateParameterFormat(template: Pick<WaTemplateRead, 'parameter_format' | 'components'>): WaTemplateParameterFormat {
  if (!!template?.parameter_format) {
    return template.parameter_format;
  }
  const texts = ((template?.components ?? []) as Array<WaTemplateComponentRead>)
    .filter((component) => component.type === 'HEADER' || component.type === 'BODY')
    .map((component) => component.text);
  const parameters = texts.reduce((all, text) => [...all, ...extractWaTemplateParameters(text)], new Array<string>());
  return parameters.some((parameter) => !/^\d+$/.test(parameter)) ? 'NAMED' : 'POSITIONAL';
}

/**
 * Shapes the values typed for a component into what the send request expects: the array as-is
 * for POSITIONAL, an object keyed by placeholder name (`names` in order of appearance) for NAMED.
 */
export function buildWaTemplateParams(format: WaTemplateParameterFormat, names: Array<string>, values: Array<string>): WaTemplateParams {
  if (format !== 'NAMED') {
    return values;
  }
  const params: Record<string, string> = {};
  names.forEach((name, index) => params[name] = values[index]);
  return params;
}

/** Substitutes each `{{marker}}` by its value in the preview; markers without a value stay visible. */
export function applyWaTemplateParams(text: string, names: Array<string>, values: Array<string>): string {
  let result = text ?? '';
  names.forEach((name, index) => {
    if (!!values[index]) {
      result = result.replace(new RegExp(`\\{\\{\\s*${name}\\s*\\}\\}`, 'g'), values[index]);
    }
  });
  return result;
}

/** Values of a send payload as a flat list, whichever shape it carries. */
export function waTemplateParamValues(params: WaTemplateParams): Array<string> {
  if (!params) {
    return [];
  }
  return Array.isArray(params) ? params : Object.values(params);
}
